import React from 'react';
import { colors as theme } from '../theme';

const PrivacyPolicy = ({ onBack }) => {
  return (
    <div style={{
      backgroundColor: theme.surface1, minHeight: '100vh',
      padding: '30px', maxWidth: '720px', margin: '0 auto',
      fontFamily: "'Inter', sans-serif", color: theme.text, lineHeight: '1.7'
    }}>
      <button
        onClick={onBack}
        style={{
          background: 'transparent', border: `1px solid ${theme.surface3}`,
          color: theme.gold, padding: '8px 16px', borderRadius: '8px',
          cursor: 'pointer', fontSize: '14px', marginBottom: '28px'
        }}
      >
        ← Back
      </button>

      <h1 style={{ color: theme.gold, fontSize: '28px', marginBottom: '4px' }}>Privacy Policy</h1>
      <p style={{ color: theme.textMuted, fontSize: '13px', marginBottom: '28px' }}>
        The Atone Academy · Last updated: 18 October 2026
      </p>

      <Section title="1. What We Collect">
        <p>We collect information you provide directly when you sign up and use the app:</p>
        <ul>
          <li>Name and email address</li>
          <li>Body weight measurements you log</li>
          <li>Workout logs, sets, reps, and weights</li>
          <li>Monthly check-in photos and messages you submit</li>
          <li>Program and training history</li>
          <li>If you choose to give them: body scans (weight, body fat and muscle), and in Cipher Nutrition, health conditions and GLP-1 medication details</li>
        </ul>
      </Section>

      <Section title="2. How We Use Your Data">
        <p>Your data is used to:</p>
        <ul>
          <li>Deliver your training programs and track your progress</li>
          <li>Allow your assigned coach to monitor and adjust your training</li>
          <li>Generate statistics and performance insights for you</li>
          <li>Improve the platform experience</li>
        </ul>
        <p>We do not sell your personal data to third parties.</p>
      </Section>

      <Section title="3. Who Can See Your Data">
        <p>
          Your <strong>assigned coach</strong> can see your workout logs, progress stats, body weight, and check-in photos.
          If you have no coach of your own, Cipher's lead coach is your coach.
        </p>
        <p>
          Your health information (body scans, health conditions and GLP-1 medication details) is seen by your coach and
          Cipher's lead coach <strong>only if you agree</strong>. You are asked separately, and you can change your answer
          at any time in Settings. Eating-disorder history is never shown to anyone at Cipher.
        </p>
        <p>
          Other clients and other coaches cannot see your data.
        </p>
      </Section>

      <Section title="4. Data Storage">
        <p>
          Your data is stored securely in Supabase (hosted on AWS) with row-level security enforced at the database level.
          All data in transit is encrypted via HTTPS/TLS.
        </p>
        <p>
          We use OpenAI to read the numbers from a photo of a body scan sheet, if you choose to add one that way, and to
          build meal plans in Cipher Nutrition. We send only what each task needs. OpenAI does not train its models on it,
          and may keep it for up to 30 days for safety checks. Cipher does not keep the photo.
        </p>
      </Section>

      <Section title="5. Your Rights (GDPR)">
        <p>If you are based in the UK or EU, you have the right to:</p>
        <ul>
          <li><strong>Access</strong> — request a copy of all data we hold about you</li>
          <li><strong>Rectification</strong> — correct inaccurate personal data</li>
          <li><strong>Erasure</strong> — request deletion of your account and data</li>
          <li><strong>Portability</strong> — receive your data in a machine-readable format</li>
          <li><strong>Objection</strong> — object to how we process your data</li>
        </ul>
        <p>
          To exercise any of these rights, use the "Settings" page in your dashboard or contact your coach/admin directly.
        </p>
      </Section>

      <Section title="6. Data Retention">
        <p>
          We keep your data for as long as your account is active, so your training history, progress, and
          coach access all work the way you'd expect.
        </p>
        <p>
          If you'd like your account and data removed, just let us know any time via the Settings page.
          Once we receive your request, our team reviews it and processes the removal, with your personal
          data fully deleted within 30 days. We may keep anonymised, aggregate statistics (e.g. overall usage
          trends) that can no longer be linked back to you.
        </p>
      </Section>

      <Section title="7. Cookies & Similar Technologies">
        <p>
          We use browser storage (like localStorage) to keep you logged in and make the App run smoothly —
          for example, saving an in-progress workout if you lose signal, remembering where you left off, and
          not showing you the same one-time prompt twice.
        </p>
        <p>We also use two lightweight, privacy-friendly tools to keep the App reliable:</p>
        <ul>
          <li>
            <strong>Error monitoring (Sentry)</strong> — if something goes wrong in the App, a report is sent
            automatically so we can find and fix it quickly. This may include your account email, so we can
            look into issues affecting you specifically.
          </li>
          <li>
            <strong>Usage analytics (Vercel Analytics &amp; Speed Insights)</strong> — anonymised page-load
            and performance data that helps us see which parts of the App are slow or need attention. This
            isn't tied to your identity.
          </li>
        </ul>
        <p>
          We do not use advertising or marketing cookies, and we never sell or share this information with
          third parties for marketing purposes.
        </p>
      </Section>

      <Section title="8. Contact">
        <p>
          For any privacy concerns, please contact us at:{' '}
          <span style={{ color: theme.gold }}>theatoneacademy@gmail.com</span>
        </p>
      </Section>
    </div>
  );
};

const Section = ({ title, children }) => (
  <div style={{ marginBottom: '24px' }}>
    <h2 style={{ color: theme.gold, fontSize: '16px', marginBottom: '10px' }}>{title}</h2>
    <div style={{ color: '#CCCCCC', fontSize: '14px' }}>{children}</div>
  </div>
);

export default PrivacyPolicy;
